import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SEO } from '@/components/SEO';
import { blogPosts as initialBlogPosts, type BlogPost } from '@/data/blogPosts';
import { Calendar, Clock, ArrowRight, BookOpen } from 'lucide-react';
import { collection, getDocs, setDoc, doc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { getSafeBlogImageUrl } from '@/lib/utils';

export default function Blog() {
  const [posts, setPosts] = useState<BlogPost[]>(initialBlogPosts);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  useEffect(() => {
    const fetchPosts = async () => {
      if (!db) return;
      try {
        const snapshot = await getDocs(collection(db, 'blogPosts'));
        if (snapshot.empty) {
          console.log('Seeding initial blog posts to Firestore...');
          const promises = initialBlogPosts.map((post) => 
            setDoc(doc(db!, 'blogPosts', post.id), post)
          );
          await Promise.all(promises);
        } else {
          const list = snapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              ...data,
              tags: data.tags || [],
            } as BlogPost;
          });
          list.sort((a, b) => b.date.localeCompare(a.date));
          setPosts(list);
        }
      } catch (error) {
        console.error('Error fetching blog posts from Firestore:', error);
      }
    };

    fetchPosts();
  }, []);

  const categories = Array.from(new Set(posts.map(post => post.category)));

  // Filter posts by category if selected
  const filteredPosts = selectedCategory
    ? posts.filter(post => post.category === selectedCategory)
    : posts;

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Freelancer & Small Business Invoicing Blog | QuickFreeInvoice"
        description="Discover invoicing best practices, freelancing guides, tax calculation rules (VAT, GST, SST), payment terms (Net 30), and expert tips to get paid faster."
        keywords="invoicing tips, freelance advice, small business billing blog, invoice best practices, net 30 payment terms, vat gst tax guide, business billing resources, get paid faster freelancer"
      />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary to-secondary text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <BookOpen className="h-8 w-8" />
              <h1 className="font-heading text-4xl md:text-5xl font-bold">
                Free Invoicing Guides, Tips &amp; Business Resources — QuickFreeInvoice Blog
              </h1>
            </div>
            <p className="text-xl text-indigo-100">
              Expert tips, guides, and best practices to help you create better invoices, 
              get paid faster, and grow your business.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 mb-12 justify-center">
            <Badge 
              variant={selectedCategory === null ? "default" : "outline"} 
              className="text-sm px-4 py-2 cursor-pointer transition-colors"
              onClick={() => setSelectedCategory(null)}
            >
              All Articles
            </Badge>
            {categories.map(category => (
              <Badge 
                key={category}
                variant={selectedCategory === category ? "default" : "outline"} 
                className="text-sm px-4 py-2 cursor-pointer transition-colors"
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </Badge>
            ))}
          </div>

          {filteredPosts.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No articles found in this category.
            </div>
          ) : (
            <>
              {/* Featured Post */}
              <div className="mb-16">
                <Card className="overflow-hidden hover:shadow-xl transition-shadow">
                  <div className="grid md:grid-cols-2 gap-6">
                    {filteredPosts[0].image ? (
                      <div className="relative h-64 md:h-auto overflow-hidden">
                        <img 
                          src={getSafeBlogImageUrl(filteredPosts[0].image)} 
                          alt={filteredPosts[0].title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center p-8">
                        <div className="text-center">
                          <BookOpen className="h-24 w-24 text-primary mx-auto mb-4" />
                          <h3 className="text-2xl font-bold text-gray-800">Featured Article</h3>
                        </div>
                      </div>
                    )}
                    <div className="p-6 flex flex-col justify-center">
                      <div className="flex items-center gap-4 mb-3 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {new Date(filteredPosts[0].date).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {filteredPosts[0].readTime}
                        </div>
                      </div>
                      <Badge className="w-fit mb-3">{filteredPosts[0].category}</Badge>
                      <h2 className="font-heading text-2xl md:text-3xl font-bold mb-3">
                        {filteredPosts[0].title}
                      </h2>
                      <p className="text-muted-foreground mb-4">
                        {filteredPosts[0].excerpt}
                      </p>
                      <Link to={`/blog/${filteredPosts[0].slug}`}>
                        <Button>
                          Read Full Article
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Blog Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredPosts.slice(1).map(post => (
                  <Card key={post.id} className="flex flex-col hover:shadow-xl transition-shadow">
                    {post.image ? (
                      <div className="relative h-48 overflow-hidden rounded-t-xl">
                        <img 
                          src={getSafeBlogImageUrl(post.image)} 
                          alt={post.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-8 flex items-center justify-center">
                        <BookOpen className="h-16 w-16 text-primary" />
                      </div>
                    )}
                    <CardHeader>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="secondary">{post.category}</Badge>
                        <span className="text-sm text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {post.readTime}
                        </span>
                      </div>
                      <CardTitle className="line-clamp-2 hover:text-primary transition-colors">
                        <Link to={`/blog/${post.slug}`}>
                          {post.title}
                        </Link>
                      </CardTitle>
                      <CardDescription className="line-clamp-3">
                        {post.excerpt}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="mt-auto">
                      <div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {new Date(post.date).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </div>
                        <span>{post.author}</span>
                      </div>
                      <Link to={`/blog/${post.slug}`}>
                        <Button variant="outline" className="w-full">
                          Read More
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <Card className="bg-gradient-to-r from-primary to-secondary text-white">
            <CardContent className="p-12 text-center">
              <h2 className="font-heading text-3xl font-bold mb-4">
                Ready to Create Your First Invoice?
              </h2>
              <p className="text-xl text-indigo-100 mb-8 max-w-2xl mx-auto">
                Put these tips into action. Start creating professional invoices in minutes with QuickFreeInvoice.
              </p>
              <Link to="/register">
                <Button size="lg" className="bg-white text-primary hover:bg-gray-100">
                  Get Started Free
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
